import { Head, router } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { CreatorAnalyticsNoOauthCard } from "@/components/crew/analytics/creator-analytics-no-oauth-card"
import { CreatorAnalyticsReliabilityCard } from "@/components/crew/analytics/creator-analytics-reliability-card"
import { CreatorAnalyticsSyncingCard } from "@/components/crew/analytics/creator-analytics-syncing-card"
import { CreatorAnalyticsTimeseriesCard } from "@/components/crew/analytics/creator-analytics-timeseries-card"
import { CreatorAnalyticsWindowToggle } from "@/components/crew/analytics/creator-analytics-window-toggle"
import { CreatorAnalyticsIdentityHeader } from "@/components/crew/creator-analytics-identity-header"
import AppLayout from "@/layouts/app-layout"
import type {
  CreatorAnalyticsProps,
  CreatorAnalyticsWindow,
} from "@/types/creator-analytics"

/**
 * OKS-64 — `Brand/Crew/Analytics` Inertia page.
 *
 * Layout per UX Spec §2:
 *
 *   - identity header (avatar + handle + link-state cue) + 2-tab strip
 *   - filter row (right-aligned window toggle)
 *   - reliability card (renders in BOTH OAuth + manual modes)
 *   - time-series section — branches on (oauth_state, snapshot_count):
 *       active + snapshots>0       → time-series card with both charts
 *       active + 0 snapshots       → syncing card
 *       refresh_needed             → time-series card + amber pill
 *       revoked                    → time-series card + destructive pill
 *       none                       → no-OAuth empty card
 *
 * Window-change handler invokes Inertia partial reload with
 * `only: ["window", "reliability", "timeseries"]` (TRD K16 + OKS-635)
 * — preserves scroll, URL query string updates. The `window` echo is
 * re-fetched (echo-first) so the controlled toggle `value` re-checks
 * the active pill on selection instead of lagging on a stale echo.
 */
export default function CreatorAnalytics({
  membership,
  window,
  oauth_state,
  reliability,
  timeseries,
}: CreatorAnalyticsProps) {
  const { t } = useTranslation()

  const handleWindowChange = (next: CreatorAnalyticsWindow) => {
    router.get(
      membership.analytics_path,
      { window: next },
      {
        only: ["window", "reliability", "timeseries"],
        preserveScroll: true,
        preserveState: false,
      },
    )
  }

  const timeseriesSection = renderTimeseriesSection({
    membership,
    oauth_state,
    timeseries,
  })

  return (
    <AppLayout>
      <Head title={t("crew.analytics.head_title")} />

      <div
        data-testid="creator-analytics-page"
        className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-6 sm:px-6"
      >
        <CreatorAnalyticsIdentityHeader membership={membership} />

        <div className="flex justify-end">
          <CreatorAnalyticsWindowToggle
            value={window}
            onChange={handleWindowChange}
          />
        </div>

        <CreatorAnalyticsReliabilityCard reliability={reliability} />

        {timeseriesSection}
      </div>
    </AppLayout>
  )
}

function renderTimeseriesSection({
  membership,
  oauth_state,
  timeseries,
}: Pick<CreatorAnalyticsProps, "membership" | "oauth_state" | "timeseries">) {
  switch (oauth_state) {
    case "none":
      return (
        <CreatorAnalyticsNoOauthCard
          messagesPath={membership.crew_message_path}
        />
      )
    case "active":
      if (timeseries.snapshot_count === 0) {
        return <CreatorAnalyticsSyncingCard />
      }
      return <CreatorAnalyticsTimeseriesCard timeseries={timeseries} />
    case "refresh_needed":
    case "revoked":
      // Even when the connection is stale/revoked, the user wants to
      // see the last-known data with a contextual pill (UX Spec §3).
      // The card handles the pill render internally based on
      // timeseries.oauth_state.
      return <CreatorAnalyticsTimeseriesCard timeseries={timeseries} />
    default:
      return null
  }
}
